/**
 * Marketer View top bar (SYR-68).
 *
 * Sidebar toggle · brand chip (a dropdown when >1 brand, plain text when one —
 * per the hide-single-choice-picker rule) · global scope pill · messages · user
 * chip. The scope pill is the persistent data-window control.
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useScopedNavigate } from '../useScopedNavigate';
import { Avatar, Badge, Button, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MailOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { Utils } from '../../../utils/utils';
import { useGetInboxStatsQuery } from '../../../services/nestjs-base-api';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { ScopePill } from './ScopePill';
import { THEME } from '../config/theme';

const { Text } = Typography;

interface MarketerTopBarProps {
  collapsed: boolean;
  isMobile: boolean;
  onToggle: () => void;
  onOpenScope: () => void;
}

export const MarketerTopBar: React.FunctionComponent<MarketerTopBarProps> = ({
  collapsed,
  isMobile,
  onToggle,
  onOpenScope,
}) => {
  const intl = useIntl();
  const navigate = useScopedNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { brands, activeBrand } = useMarketerScope();
  const marketerRoot = useMarketerRoot();

  const { data: inboxStats } = useGetInboxStatsQuery(undefined, {
    skip: !Utils.userIsOrganisationAdmin(user),
    pollingInterval: 30000,
  });

  // Sub-path within the brand root, preserved when switching brand.
  const subPath = activeBrand
    ? location.pathname.split(`${marketerRoot}/${activeBrand.hash}`)[1] ?? ''
    : '';

  const brandMenu: MenuProps = {
    items: brands.map((b) => ({ key: b.hash, label: b.name })),
    onClick: ({ key }) => navigate(`${marketerRoot}/${key}${subPath}`),
  };

  const userMenu: MenuProps = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: intl.formatMessage({ id: 'layout.dashboard.user.profile' }),
      },
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: intl.formatMessage({ id: 'layout.dashboard.user.settings' }),
      },
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        danger: true,
        label: intl.formatMessage({ id: 'layout.dashboard.user.signOut' }),
      },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') logout();
      else if (key === 'profile') navigate('/dashboard/profile');
      else if (key === 'settings') navigate('/dashboard/organisation');
    },
  };

  const brandName =
    activeBrand?.name ??
    user?.organisation?.name ??
    intl.formatMessage({
      id: 'layout.dashboard.brandName',
      defaultMessage: 'BrandScience',
    });

  return (
    <div
      style={{
        height: 65,
        background: '#fff',
        borderBottom: `1px solid ${THEME.border}`,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '0 16px' : '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          type="text"
          icon={
            isMobile ? (
              <MenuUnfoldOutlined />
            ) : collapsed ? (
              <MenuUnfoldOutlined />
            ) : (
              <MenuFoldOutlined />
            )
          }
          onClick={onToggle}
          style={{ fontSize: 16, width: 40, height: 40 }}
        />
        {/* Brand chip — dropdown only when >1 brand (hide single-choice picker). */}
        {brands.length > 1 ? (
          <Dropdown menu={brandMenu} trigger={['click']}>
            <button
              type="button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 600,
                color: '#1f2937',
                maxWidth: '52vw',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {brandName}
              </span>
              <DownOutlined style={{ fontSize: 12, color: THEME.textMuted, flexShrink: 0 }} />
            </button>
          </Dropdown>
        ) : (
          <Text
            style={{
              fontSize: isMobile ? 16 : 20,
              fontWeight: 600,
              color: '#1f2937',
              maxWidth: isMobile ? '52vw' : 320,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {brandName}
          </Text>
        )}
        {!isMobile && <ScopePill onOpen={onOpenScope} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {isMobile && <ScopePill onOpen={onOpenScope} />}
        <Badge
          count={Utils.userIsOrganisationAdmin(user) ? inboxStats?.unread ?? 0 : 0}
          size="small"
          showZero={false}
        >
          <Button
            type="text"
            icon={<MailOutlined />}
            onClick={() =>
              navigate(
                `/dashboard/profile?tab=${
                  Utils.userIsOrganisationAdmin(user)
                    ? 'messages'
                    : 'notifications'
                }`,
              )
            }
            style={{ fontSize: 16, width: 40, height: 40 }}
          />
        </Badge>
        <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              padding: '6px 8px',
              borderRadius: 8,
            }}
          >
            <Avatar
              style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              }}
              icon={<UserOutlined />}
            />
            {!isMobile && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={{ fontWeight: 500, color: '#1f2937', lineHeight: 1.2 }}>
                  {user?.email?.split('@')[0] ?? 'User'}
                </Text>
                <Text style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.2 }}>
                  {intl.formatMessage({ id: 'layout.dashboard.roleAdmin' })}
                </Text>
              </div>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
